import {priority} from '@priority-software/priority-proto-api/lib';
import {TreeNodeProps} from '@priority-software/priority-style-react';
import {
    SingleSeriesChartTypesOptions,
    MultiSeriesChartTypesOptions,
    ChartDimensions
} from './chartConsts';
import {Record} from '../../stores/Form/Record';
import {AnalysisType} from './services/analysisService';

export type OpenExprDialogState = {
    index: null | number;
    column: null | ReportColumn;
    isOpen: boolean;
};

export type DateProps = {
    type: string;
    dateFormat: string;
    isGrouped: boolean;
};

export interface ExplanatoryReport extends priority.netitems.nrest.IReportTitle {
    value: string;
    label: string;
    chartInputs: ReportColumn[];
}

export interface ReportInputMetaData extends priority.netitems.nrest.IReportInput {
    isDataplot: boolean;
    inputIndex: number;
    dateProps?: DateProps;
    dataplotDimension?: ChartDimensions;
}

export interface IChartConfig {
    baseForm: string;
    chartName: string;
    subTitle: string;
    explanatoryReport?: ExplanatoryReport;
    active: boolean;
    share: boolean;
    type: string;
    xAxis: ReportColumn;
    yAxis: ReportColumn;
    topX: string;
    bubbleConfig?: IBubbleConfig;
    series?: ReportColumn;
    scales?: Record;
    multiCompany?: boolean;
}

export interface ChartConfig extends IChartConfig {
    type: SingleSeriesChartTypesOptions | MultiSeriesChartTypesOptions;
    data: ReportData;
    hasRTLText?: boolean;
    numberSuffix?: string;
    lowerLimit: string;
    upperLimit: string;
    dataPlotEventHandler: () => void;
}

export interface IBubbleConfig {
    minX?: string;
    maxX?: string;
    minY?: string;
    maxY?: string;
    category?: ReportColumn;
    zBubbleSize?: ReportColumn;
}

export interface IReport {
    title: string | null;
    id: number | Long | null;
    nodeId: string | null;
    isGeneral: boolean;
    createdDate: string;
    updateTime: string;
    userName: string | null;
    baseForm: string;
    reportType: priority.netitems.nrest.RepType;
    isOldReport: boolean;
    isActive: boolean;
}

export enum Pages {
    main,
    report
}

export interface ReportColumn extends priority.netitems.nrest.IReportCol {
    error?: ColumnsFieldsNames[];
    isDescending?: boolean;
    ShowMine?: boolean;
    emptyExpression?: boolean;
    displayedExp?: string;
}

export enum ColumnsFieldsNames {
    ColId = 'ColId',
    Title = 'Title',
    Width = 'Width',
    Input = 'Input',
    Group = 'Group',
    Header = 'Header',
    AxisMode = 'AxisMode',
    Hide = 'Hide',
    Sort = 'Sort',
    isDescending = 'isDescending',
    Expr = 'Expr',
    IsExpression = 'IsExpression',
    IsCompany = 'IsCompany',
    Type = 'Type',
    Func = 'Func',
    GroupFunc = 'GroupFunc',
    DateFormat = 'DateFormat',
    NodeId = 'NodeId',
    OrigTitle = 'OrigTitle',
    ShowMine = 'ShowMine',
    NumericFormat = 'NumericFormat',
    SortType = 'SortType',
    DateFunc = 'DateFunc'
}

export type ReportData = {
    Columns: priority.netitems.nrest.IDataColumn[];
    Rows: priority.netitems.nrest.IRow[];
    Companies: priority.netitems.nrest.ICompany[];
};

export type UpdateTimestamp = {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
    seconds: number;
};

export type ChartHeader = {
    Company: string;
    Inputs: priority.netitems.nrest.IDataInputInfo[];
    Updated: UpdateTimestamp;
    User: string;
};

export type MultiChartSerie = {
    seriesname: string;
    origSerieName: string;
    data: {value: number; formattedValue: string}[];
};

export type ReportParam = {
    field: string;
    name: string;
    operator: string;
    operator2: string;
    type: string;
    value: string;
    value2: string;
};

export interface SerializedReportParams extends priority.netitems.nrest.IDataInput {
    OriginalOp: string;
    OriginalOp2: string;
}

type HyperLinkDataType = {
    from: string;
    len: string;
    title: string;
    ename: string;
    type: string;
};

export type HelpData = {
    text: string;
    HyperLinks: HyperLinkDataType[];
};

export type BasicFormData = {id: string; title: string};

export type DataInclusionTreeNode = Omit<TreeNodeProps, 'nodes'> & {
    nodes?: TreeNodeProps[];
    isField?: boolean;
};

export type ChartDataSetItem = {
    name: string;
    data: {x: string; y: string; z?: string}[];
};

export type ChartAnalysisRq = {
    language: 'en' | 'he';
    analysisType: AnalysisType;
    ename: string;
    baseFormTitle: string;
    chartType: SingleSeriesChartTypesOptions | MultiSeriesChartTypesOptions;
    caption: string;
    subCaption: string;
    xAxisName: string;
    yAxisName: string;
    zAxisName?: string;
    bubbleCategory?: string;
    dataSet: ChartDataSetItem[];
};

export interface ChartAnalysisRs {
    analysisContent: string;
    error: string;
}
